import { Plugin, keyCodes, toWidget } from 'ckeditor5';
import { v4 as uuid } from 'uuid';
import { PlaceholderStore } from './store';
/**
 * NOTE: This schema is for the Orca template editor.
 * This plugin adds support for placeholders in the Orca Editor
 * Handles rendering of <span data-placeholder="...">content</span> elements.
 */
export class OrcaPlaceholderSchema extends Plugin {
    static get pluginName() {
        return 'OrcaPlaceholderSchema';
    }
    init() {
        this.store = this.editor.plugins.get(PlaceholderStore);
        this.defineSchema();
        this.defineConverters();
        this.removalCheck();
    }
    defineSchema() {
        const { schema } = this.editor.model;
        schema.register('placeholder', {
            inheritAllFrom: '$inlineObject',
            allowAttributes: ['id', 'instanceId'],
        });
    }
    removalCheck() {
        // Disallow deleting placeholders if in readonly mode
        if (this.editor.config.get('placeholders.readonly') === true) {
            const { view } = this.editor.editing;
            view.document.on('keydown', (evt, data) => {
                const selectedElement = this.editor.model.document.selection.getSelectedElement();
                if ((selectedElement === null || selectedElement === void 0 ? void 0 : selectedElement.is('element', 'placeholder')) &&
                    (data.keyCode === keyCodes.backspace || data.keyCode === keyCodes.delete)) {
                    evt.stop();
                    data.preventDefault();
                }
            }, { priority: 'high' });
        }
    }
    defineConverters() {
        const { conversion } = this.editor;
        /**
         * UPCAST: HTML → Model
         * Converts <span data-placeholder="name">content</span> from HTML/data
         * into a placeholder model element when loading content into the editor.
         */
        conversion.for('upcast').elementToElement({
            view: {
                name: 'span',
                attributes: ['data-placeholder'],
            },
            model: (viewElement, { writer }) => {
                const id = viewElement.getAttribute('data-placeholder');
                const placeholder = writer.createElement('placeholder', { id, instanceId: uuid() });
                return placeholder;
            },
            converterPriority: 'high',
        });
        /**
         * EDITING DOWNCAST: Model → View (Editor)
         * Converts placeholder model element into an editable widget view
         * for display in the editor. Wraps in toWidget() for selection/toolbar support.
         */
        conversion.for('editingDowncast').elementToElement({
            model: 'placeholder',
            view: (modelElement, { writer }) => {
                const id = modelElement.getAttribute('id');
                const placeholder = this.store.getPlaceholderFromStore(id);
                if (!placeholder) {
                    throw new Error(`Placeholder with id ${id} not found in store`);
                }
                const span = writer.createContainerElement('span', {
                    'data-placeholder': id,
                    class: 'placeholder',
                });
                writer.setCustomProperty('placeholder', true, span);
                const textNode = writer.createText('');
                writer.insert(writer.createPositionAt(span, 0), textNode);
                return toWidget(span, writer, {
                    label: 'placeholder',
                });
            },
        });
        /**
         * DATA DOWNCAST: Model → HTML
         * Converts placeholder model element back to HTML when saving/exporting content.
         * Produces clean <span data-placeholder="name">content</span> markup.
         */
        conversion.for('dataDowncast').elementToElement({
            model: 'placeholder',
            view: (modelElement, { writer }) => {
                const id = modelElement.getAttribute('id');
                const displayValue = ''; // To create empty span for the templates, we want them empty.
                const span = writer.createContainerElement('span', {
                    'data-placeholder': id,
                });
                writer.insert(writer.createPositionAt(span, 0), writer.createText(displayValue));
                return span;
            },
        });
    }
}
